import { ExpressionMatcher } from "./api";

const groupPattern = /^(when|or)\s+(.+)$/i;
const amountPattern = /^amount\s+(>=|<=|==)\s+(-?\d+(?:\.\d+)?)$/i;
const textPattern = /^(description|tag)\s+~\s+(.+)$/i;

export class BudgetRuleTextError extends Error {
    constructor(message: string, public readonly line: number) {
        super(`Line ${line}: ${message}`);
    }
}

export const formatBudgetRuleMatchers = (matchers: ExpressionMatcher[][]): string => {
    return matchers.map((matcher, index) => {
        const prefix = index === 0 ? "when" : "or";
        return `${prefix} ${matcher.map(formatMatcher).join(" and ")}`;
    }).join("\n");
};

export const parseBudgetRuleMatchers = (text: string): ExpressionMatcher[][] => {
    const lines = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0 && !line.startsWith("#"));

    return lines.map((line, index) => {
        const group = groupPattern.exec(line);
        if (!group) {
            throw new BudgetRuleTextError("Expected a when/or matcher group", index + 1);
        }
        return parseMatcherGroup(group[2], index + 1);
    });
};

const parseMatcherGroup = (raw: string, line: number): ExpressionMatcher[] => {
    const parts = splitByAnd(raw);
    if (parts.length === 0) {
        throw new BudgetRuleTextError("Expected at least one condition", line);
    }
    return parts.map(part => parseMatcher(part, line));
};

const parseMatcher = (raw: string, line: number): ExpressionMatcher => {
    const amount = amountPattern.exec(raw);
    if (amount) {
        return {
            type: "Amount",
            expression: `${amount[1]} ${amount[2]}`
        };
    }

    const text = textPattern.exec(raw);
    if (!text) {
        throw new BudgetRuleTextError("Invalid condition", line);
    }

    return {
        type: text[1].toLowerCase() === "tag" ? "Tag" : "Description",
        expression: parseConditionValue(text[2], line)
    };
};

const splitByAnd = (raw: string): string[] => {
    const parts: string[] = [];
    let current = "";
    let quoted = false;
    let escaped = false;

    for (let index = 0; index < raw.length; index++) {
        const char = raw[index];
        if (escaped) {
            current += char;
            escaped = false;
            continue;
        }
        if (char === "\\") {
            current += char;
            escaped = true;
            continue;
        }
        if (char === '"') {
            quoted = !quoted;
            current += char;
            continue;
        }
        if (!quoted && raw.slice(index, index + 5).toLowerCase() === " and ") {
            parts.push(current.trim());
            current = "";
            index += 4;
            continue;
        }
        current += char;
    }

    if (current.trim().length > 0) {
        parts.push(current.trim());
    }

    return parts;
};

const parseConditionValue = (raw: string, line: number): string => {
    const value = raw.trim();
    if (!value.startsWith('"')) {
        return value;
    }
    if (!value.endsWith('"') || value.length === 1) {
        throw new BudgetRuleTextError("Unterminated quoted value", line);
    }
    return unescapeQuoted(value.slice(1, -1));
};

const formatMatcher = (matcher: ExpressionMatcher): string => {
    if (matcher.type === "Amount") {
        return `amount ${matcher.expression}`;
    }
    return `${matcher.type.toLowerCase()} ~ "${escapeQuoted(matcher.expression)}"`;
};

const escapeQuoted = (value: string): string => {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

const unescapeQuoted = (value: string): string => {
    return value.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
};
